import { supabase } from "@/lib/supabase";
import {
  checklistDisplayName,
  checklistPhotoUrls,
  checklistRequiresPhoto,
  parseChecklistItems,
} from "@/lib/checklistJson";
import { isCancelledCleaningTask } from "@/lib/cleanerTaskQuery";
import { getChecklistCache, putChecklistCache } from "@/lib/offline/offlineDb";

export type ChecklistTaskForSection = {
  id: string;
  name: string;
  completed: boolean;
  note?: string;
  type: "recurring" | "adhoc";
  task_type?: "sop_standard" | "coordinator_single" | "long_term" | "employee_extra";
  status?: "active" | "moved";
  moved_to_task_id?: string;
  moved_to_date?: string;
  requiresPhoto?: boolean;
  photoTaken?: boolean;
  photo_required?: boolean;
  min_photos?: number;
  photo_urls?: string[];
  originTaskId?: string;
  is_extra_task?: boolean;
  section_id?: string;
};

export type CleanerChecklistQueryResult = {
  sectionId: string;
  taskIdsFingerprint: string;
  tasks: ChecklistTaskForSection[];
  actualTaskIds: string[];
  taskStatus: "pending" | "in_progress" | "completed" | null;
};

export async function fetchCleanerSectionChecklist(params: {
  sectionId: string;
  normalizedTaskIds: string[];
}): Promise<CleanerChecklistQueryResult> {
  const { sectionId, normalizedTaskIds } = params;
  const taskIdsFingerprint = [...normalizedTaskIds].sort().join(",");
  const cacheKey = `${sectionId}:${taskIdsFingerprint || "all"}`;

  if (typeof navigator !== "undefined" && !navigator.onLine) {
    const cached = await getChecklistCache<CleanerChecklistQueryResult>(cacheKey);
    if (cached) return cached;
  }

  try {
        // KRYTYCZNE: Wyczyść finalTasks przed każdym nowym pobieraniem (zapobiega "wiszącym" starym danym)
        // Ustawiamy pustą tablicę jako punkt wyjścia - tylko poprawne dane z bazy nadpiszą to
        const finalTasks: ChecklistTaskForSection[] = [];
        
        // Logowanie diagnostyczne - pokaż otwartą sekcję
        console.log(`[fetchCleanerSectionChecklist] sectionId=${sectionId}`);
        
        // Pobierz wszystkie zadania dla taskIds (wszystkie aktywne zadania)
        // WAŻNE: Pobierz również section_id, location_id, task_type i scheduled_at z każdego rekordu, aby móc filtrować po sekcji, budynku i dacie
        // Oblicz datę początku i końca dzisiaj dla filtrowania
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const todayStartISO = todayStart.toISOString();
        
        const todayEnd = new Date();
        todayEnd.setHours(23, 59, 59, 999);
        const todayEndISO = todayEnd.toISOString();
        
        const { data: tasksData, error: tasksError } = await supabase
          .from('cleaning_tasks')
          .select('id, checklist, status, created_at, section_id, location_id, task_type, coordinator_notes, scheduled_at')
          .in('id', normalizedTaskIds)
          .order('created_at', { ascending: false });

        if (tasksError) {
          console.error("[fetchCleanerSectionChecklist] Fetch error:", tasksError);
          throw new Error(tasksError.message);
        }
        
        if (!tasksData || tasksData.length === 0) {
          return {
            sectionId,
            taskIdsFingerprint,
            tasks: [],
            actualTaskIds: [],
            taskStatus: "pending",
          };
        }

        // Stale-While-Revalidate: Użyj tempData - tylko po SUKCESIE zapytania wykonaj setTasks
        // FIX: Pobierz WSZYSTKIE zadania dla sekcji na dziś (nie filtruj po status != 'done')
        // - sop_standard: tylko z dzisiejszą datą (scheduled_at = dzisiaj) - wszystkie statusy oprócz cancelled
        // - long_term: widoczne jeśli scheduled_at <= DZISIAJ - wszystkie statusy oprócz cancelled
        // - coordinator_single / employee_extra: otwarte i ukończone dziś; cancelled (soft delete) ukryte
        // Ukończone zadania będą wyświetlane jako wyszarzone/przekreślone z przyciskiem "Cofnij"
        const tempTasksData = tasksData.filter(task => {
          if (isCancelledCleaningTask(task.status)) return false;

          // coordinator_single i employee_extra: wszystkie zadania (niezależnie od statusu)
          if (task.task_type === 'coordinator_single' || task.task_type === 'employee_extra') {
            return true; // Pobierz wszystkie, nawet ukończone
          }
          
          // sop_standard: tylko dzisiaj (wszystkie statusy, łącznie z done)
          if (task.task_type === 'sop_standard') {
            if (!task.scheduled_at) return false;
            const scheduledDate = new Date(task.scheduled_at);
            const scheduledDateOnly = new Date(scheduledDate.getFullYear(), scheduledDate.getMonth(), scheduledDate.getDate());
            const todayOnly = new Date(todayStart.getFullYear(), todayStart.getMonth(), todayStart.getDate());
            return scheduledDateOnly.getTime() === todayOnly.getTime();
          }
          
          // long_term: widoczne jeśli scheduled_at <= DZISIAJ (wszystkie statusy, łącznie z done)
          if (task.task_type === 'long_term') {
            if (!task.scheduled_at) return false;
            const scheduledDate = new Date(task.scheduled_at);
            return scheduledDate <= todayEnd;
          }
          
          // Dla innych typów zadań (fallback) - pokaż wszystkie
          return true;
        });

        // Zapisz faktyczne task_ids do użycia w innych funkcjach (tylko jeśli dane są poprawne)
        const currentTaskIds = tempTasksData.map(t => t.id);

        // Zapisz status zadania (najwyższy priorytet: in_progress > pending > done) - używamy tempTasksData
        const statuses = tempTasksData.map(t => t.status).filter(Boolean) as ('pending' | 'in_progress' | 'completed' | 'done')[];
        let status: 'pending' | 'in_progress' | 'completed' | null = 'pending';
        if (statuses.includes('in_progress')) status = 'in_progress';
        else if (statuses.includes('pending')) status = 'pending';
        else if (statuses.includes('completed') || statuses.includes('done')) status = 'completed';
        // Sortuj zadania po created_at (najnowsze pierwsze) - nowsze zadania mają priorytet przy scalaniu
        const sortedTasks = [...tempTasksData].sort((a, b) => {
          const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
          const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
          if (timeA !== timeB) return timeB - timeA; // Najnowsze pierwsze
          return b.id.localeCompare(a.id);
        });

        // Mapa unikalnych zadań - używamy taskRecord.id jako klucza dla wszystkich zadań
        const uniqueChecklistMap = new Map<string, { task: any; originTaskId: string; taskTypeFromRecord?: string }>();
        
        const currentSectionId = sectionId;
        const normalizedSectionId = currentSectionId ? String(currentSectionId).toLowerCase() : null;

        // Przejdź przez wszystkie zadania i przetwórz je
        for (const taskRecord of sortedTasks) {
          // UPROSZCZONA LOGIKA FILTROWANIA: Użyj section_id z kolumny bazy danych
          const taskSectionId = taskRecord.section_id ? String(taskRecord.section_id).toLowerCase() : null;
          
          // Jeśli section_id się nie zgadza, pomiń ten rekord
          if (!taskSectionId || !normalizedSectionId || taskSectionId !== normalizedSectionId) {
            console.log(`[fetchCleanerSectionChecklist] Pominięto zadanie ${taskRecord.id} - section_id nie pasuje (${taskSectionId} !== ${normalizedSectionId})`);
            continue;
          }

          // Obsługa typów zadań Single Tasks (coordinator_single, employee_extra)
          if (taskRecord.task_type === 'coordinator_single' || taskRecord.task_type === 'employee_extra') {
            const items = parseChecklistItems(taskRecord.checklist);
            const item = items[0] ?? {};
            const taskName = taskRecord.coordinator_notes?.trim() || checklistDisplayName(item);
            
            if (!taskName) {
              console.log(`[fetchCleanerSectionChecklist] Pominięto zadanie ${taskRecord.id} - brak nazwy`);
              continue;
            }

            const isCompleted = taskRecord.status === 'done';
            const photo_required = checklistRequiresPhoto(item, taskRecord.checklist);
            const photo_urls = checklistPhotoUrls(item, taskRecord.checklist);
            
            // Użyj taskRecord.id jako klucza unikalnego (deduplikacja)
            const uniqueKey = taskRecord.id;
            
            if (!uniqueChecklistMap.has(uniqueKey)) {
              uniqueChecklistMap.set(uniqueKey, {
                task: {
                  id: taskRecord.id,
                  name: taskName,
                  task_name: taskName,
                  completed: isCompleted,
                  section_id: taskRecord.section_id,
                  task_type: taskRecord.task_type,
                  type: 'adhoc',
                  note: (item.note !== undefined && item.note !== null) ? String(item.note) : '',
                  photo_required,
                  photo_urls,
                },
                originTaskId: taskRecord.id,
                taskTypeFromRecord: taskRecord.task_type
              });
              console.log(`[fetchCleanerSectionChecklist] Dodano zadanie single: ${taskRecord.task_type} (${taskRecord.id}) - ${taskName}`);
            }
            continue;
          }

          // Obsługa zadań standardowych (sop_standard, long_term) - mają checklist w JSON
          const checklistData = taskRecord.checklist;
          if (!checklistData) {
            console.log(`[fetchCleanerSectionChecklist] Pominięto zadanie ${taskRecord.id} - brak checklist`);
            continue;
          }

          // Konwertuj checklist na tablicę zadań
          const checklistArray = parseChecklistItems(checklistData);

          // Dla zadań standardowych, przetwórz każdy element z checklist
          // Używamy kombinacji taskRecord.id + item.id jako klucza dla każdego zadania
          for (const item of checklistArray) {
            const taskName = item.task_name || item.name || item.id || '';
            
            if (!taskName) continue; // Pomijaj zadania bez nazwy
            
            // Użyj kombinacji taskRecord.id + item.id jako klucza unikalnego (deduplikacja)
            // Dla zadań standardowych każdy element checklist ma swój unikalny klucz
            const itemId = item.id || `${taskRecord.id}_${taskName}`;
            const uniqueKey = `${taskRecord.id}_${itemId}`;
            
            if (!uniqueChecklistMap.has(uniqueKey)) {
              // Status: jeśli status === 'completed' lub 'done', ustaw completed: true
              const isCompleted = taskRecord.status === 'done' || item.completed === true;
              
              uniqueChecklistMap.set(uniqueKey, { 
                task: {
                  ...item,
                  id: item.id || taskRecord.id,
                  name: taskName,
                  task_name: taskName,
                  completed: isCompleted,
                  section_id: taskRecord.section_id,
                  task_type: taskRecord.task_type || item.task_type,
                }, 
                originTaskId: taskRecord.id,
                taskTypeFromRecord: taskRecord.task_type || undefined
              });
            } else {
              // Zadanie już istnieje w mapie - scal dane
              const existing = uniqueChecklistMap.get(uniqueKey)!;
              
              // ZASADA "RAZ WYKONANE, ZAWSZE WYKONANE"
              const existingCompleted = existing.task.completed === true;
              const itemCompleted = item.completed === true || taskRecord.status === 'done';
              const mergedCompleted = existingCompleted || itemCompleted || false;
              
              // Scal notatki: preferuj najnowszą niepustą notatkę
              const existingNote = existing.task.note;
              const itemNote = item.note;
              const existingNoteTrimmed = (existingNote !== undefined && existingNote !== null) ? String(existingNote).trim() : '';
              const itemNoteTrimmed = (itemNote !== undefined && itemNote !== null) ? String(itemNote).trim() : '';
              const existingNoteIsNotEmpty = existingNoteTrimmed !== '';
              const itemNoteIsNotEmpty = itemNoteTrimmed !== '';
              
              const mergedNote = existingNoteIsNotEmpty
                ? existingNote
                : (itemNoteIsNotEmpty ? itemNote : '');
              
              // Scal zdjęcia: połącz tablice zdjęć (duplikaty usunięte)
              const existingPhotoUrls = existing.task.photo_urls || [];
              const itemPhotoUrls = item.photo_urls || [];
              const mergedPhotoUrls = [...new Set([...existingPhotoUrls, ...itemPhotoUrls])];
              
              // Task type: priorytet - task_type z JSON checklisty > task_type z rekordu
              const taskTypeFromItem = item.task_type;
              const taskTypeFromExisting = existing.taskTypeFromRecord;
              const taskTypeFromCurrentRecord = taskRecord.task_type;
              const mergedTaskType = taskTypeFromItem || taskTypeFromExisting || taskTypeFromCurrentRecord || undefined;
              
              uniqueChecklistMap.set(uniqueKey, { 
                task: { 
                  ...item,
                  ...existing.task,
                  completed: mergedCompleted,
                  note: mergedNote,
                  photo_urls: mergedPhotoUrls,
                  task_type: mergedTaskType,
                }, 
                originTaskId: existing.originTaskId,
                taskTypeFromRecord: mergedTaskType
              });
            }
          }
        }

        // Konwertuj Mapę na tablicę ChecklistTask z informacją o originTaskId
        // WAŻNE: Zachowaj wszystkie pola z JSONB, aby nie utracić danych po odświeżeniu
        const helperMapTaskToChecklistTask = (item: any, originTaskId: string, taskTypeFromRecord?: string): ChecklistTaskForSection => {
          // Normalizuj completed - upewnij się że zawsze jest boolean
          const completed = item.completed === true || item.completed === 'true';
          
          // Normalizuj note - zachowaj pusty string zamiast null/undefined
          const note = (item.note !== undefined && item.note !== null) ? String(item.note) : '';
          
          // Normalizuj photo_urls - upewnij się że jest to tablica
          const photo_urls = Array.isArray(item.photo_urls) ? item.photo_urls : [];
          
          // Task type: priorytet - task_type z JSON checklisty > task_type z rekordu cleaning_tasks > undefined
          // Jeśli task_type jest w JSON checklisty (item.task_type), użyj go, w przeciwnym razie użyj task_type z rekordu
          const task_type = item.task_type || taskTypeFromRecord || undefined;
          
          // FIX: Jeśli task_type === 'employee_extra', automatycznie ustaw is_extra_task: true
          // Zapewnia to poprawne mapowanie zadań dodatkowych
          const is_extra_task = task_type === 'employee_extra' 
            ? true 
            : (item.is_extra_task === true || item.is_extra_task === 'true');
          
          // Status: zachowaj status z JSON checklisty (może być 'moved' dla przeniesionych zadań)
          const status = item.status === 'moved' ? 'moved' : 'active';
          
          // LOGIKA WYŚWIETLANIA NAZW ZADAŃ:
          // - Dla coordinator_single i employee_extra: użyj coordinator_notes jako głównego tytułu, fallback do checklist[0].name
          // - Dla sop_standard i long_term: użyj nazwy z item.name (item to już element z checklisty)
          let taskName = '';
          
          // KROK 1: Dla coordinator_single i employee_extra - użyj coordinator_notes
          if (task_type === 'coordinator_single' || task_type === 'employee_extra') {
            if (originTaskId) {
              const sourceTask = tempTasksData.find(t => t.id === originTaskId);
              if (sourceTask?.coordinator_notes) {
                taskName = sourceTask.coordinator_notes.trim();
              }
            }
            // Fallback dla coordinator_single/employee_extra jeśli brak coordinator_notes - użyj checklist[0].name lub item.name
            if (!taskName) {
              // Sprawdź czy sourceTask ma checklist z nazwą
              if (originTaskId) {
                const sourceTask = tempTasksData.find(t => t.id === originTaskId);
                if (sourceTask) {
                  taskName = parseChecklistItems(sourceTask.checklist)[0]?.name || '';
                }
              }
              // Ostateczny fallback
              if (!taskName) {
                taskName = item.task_name || item.name || item.id || '';
              }
            }
          } else {
            // KROK 2: Dla sop_standard i long_term - użyj item.name (item to już element z checklisty)
            taskName = item.name || item.task_name || item.id || '';
          }
          
          // Dla zadań coordinator_single i employee_extra: jeśli item.id puste → originTaskId
          // Dla sop_standard i long_term: jeśli item.id puste → originTaskId_taskName (unikalny klucz dla SOP)
          let taskId: string;
          if ((task_type === 'coordinator_single' || task_type === 'employee_extra') && !item.id) {
            taskId = originTaskId || '';
          } else if (item.id) {
            taskId = item.id;
          } else if (taskName && originTaskId) {
            taskId = `${originTaskId}_${taskName}`;
          } else {
            taskId = '';
          }
          
          return {
            id: taskId,
            name: taskName,
            completed: completed,
            note: note,
            type: (item.type || 'recurring') as 'recurring' | 'adhoc',
            task_type: task_type as 'sop_standard' | 'coordinator_single' | 'long_term' | 'employee_extra' | undefined,
            status: status as 'active' | 'moved', // FIX: Zachowaj status 'moved' z JSON checklisty
            moved_to_task_id: item.moved_to_task_id, // ID kopii na docelową datę (dla cofnięcia)
            moved_to_date: item.moved_to_date, // Data docelowa przeniesienia (ISO string)
            photo_required: item.photo_required === true || item.photo_required === 'true',
            min_photos: item.min_photos ?? (item.photo_required ? 1 : 0),
            photo_urls: photo_urls,
            originTaskId: originTaskId, // Zachowaj informację o źródle dla aktualizacji
            is_extra_task: is_extra_task,
            section_id: item.section_id || sectionId,
            // Legacy fields for backward compatibility
            requiresPhoto: item.photo_required === true || item.photo_required === 'true',
            photoTaken: photo_urls.length > 0,
          };
        };

        // Konwertuj Mapę na tablicę ChecklistTask z informacją o originTaskId i task_type
        // Task type z rekordu cleaning_tasks jest używany w logice generowania checklist
        // FIX: Wszystkie zadania (w tym employee_extra) są już zawarte w normalizedTaskIds i przetworzone przez uniqueChecklistMap
        // Usunięto nadmiarowe zapytanie dla employee_extra, aby uniknąć duplikacji
        const mappedTasks = Array.from(uniqueChecklistMap.values()).map(
          ({ task, originTaskId, taskTypeFromRecord }) => helperMapTaskToChecklistTask(task, originTaskId, taskTypeFromRecord)
        );

        // FIX: Eliminacja duplikatów - upewnij się, że każde zadanie (po ID) występuje tylko raz
        // Używamy Map z ID zadania jako kluczem, aby wyeliminować duplikaty
        const uniqueTasksMap = new Map<string, ChecklistTaskForSection>();
        for (const task of mappedTasks) {
          // Jeśli zadanie z tym ID już istnieje, zachowaj to z najnowszym originTaskId (priorytet)
          if (!uniqueTasksMap.has(task.id) || (task.originTaskId && !uniqueTasksMap.get(task.id)?.originTaskId)) {
            uniqueTasksMap.set(task.id, task);
          }
        }
        
        // Nadpisz finalTasks danymi z bazy (już zdefiniowane na początku jako pusta tablica)
        finalTasks.length = 0; // Wyczyść tablicę
        finalTasks.push(...Array.from(uniqueTasksMap.values()));

        // FIX: Usunięto osobne zapytanie dla employee_extra - wszystkie zadania są już w normalizedTaskIds
        // Zadania employee_extra są automatycznie oznaczone jako is_extra_task: true w helperMapTaskToChecklistTask

        // Loguj szczegóły zadań bez szablonów
        const tasksWithoutTemplates = finalTasks.filter(t => 
          t.task_type === 'coordinator_single' || t.task_type === 'employee_extra'
        );
        console.log(`[fetchCleanerSectionChecklist] ✅ Scalono ${tempTasksData.length} zadań, znaleziono ${finalTasks.length} unikalnych zadań dla sekcji ${currentSectionId} (po deduplikacji)`);
        if (tasksWithoutTemplates.length > 0) {
          console.log(`[fetchCleanerSectionChecklist] 📋 Zadania bez szablonów: ${tasksWithoutTemplates.length}`, 
            tasksWithoutTemplates.map(t => ({ id: t.id, name: t.name, task_type: t.task_type }))
          );
        }

        const result = {
          sectionId,
          taskIdsFingerprint,
          tasks: finalTasks,
          actualTaskIds: currentTaskIds,
          taskStatus: status,
        };
        void putChecklistCache(cacheKey, result);
        return result;
      } catch (error) {
        console.error("[fetchCleanerSectionChecklist] Error:", error);
        if (error instanceof Error && (
          error.name === "AbortError" ||
          error.name === "DOMException" ||
          error.message === "signal is aborted without reason"
        )) {
          console.info("[fetchCleanerSectionChecklist] Aborted fetch.");
          throw error;
        }
        const cached = await getChecklistCache<CleanerChecklistQueryResult>(cacheKey);
        if (cached) {
          console.warn("[fetchCleanerSectionChecklist] Używam cache offline po błędzie sieci.");
          return cached;
        }
        throw error instanceof Error ? error : new Error(String(error));
      }
}
